import { useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
} from '@react-pdf/renderer';
import parse from 'html-react-parser';
import ibmLogo from '../public/images/ibmlogo.png';
import cratImage from '../public/images/crat.png';
import { PDFData } from '../hooks/useData';
import { imageToBase64 } from '../lib/imageToBase64';
import { SKELETON_IMAGE } from '../constants/default';

interface RadarData {
  product: string;
  feature: string;
  score: number;
}

interface pdfGenerateProps {
  pdfData?: PDFData | null;
  radarData: RadarData[];
  chartImage: string;
  scorePercentage: number;
}

export const PDFGenerate = ({
  pdfData,
  radarData,
  chartImage,
  scorePercentage,
}: pdfGenerateProps) => {
  Font.registerHyphenationCallback((word) => [word]);

  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#f4f4f4',
      paddingTop: 60,
      border: 'solid white 2px',
    },
    section: {
      fontSize: 12,
      textAlign: 'justify',
      width: 500,
      marginLeft: 50,
      marginRight: 50,
      lineHeight: 1.3,
    },
    title: { fontSize: 14, paddingBottom: 25, fontFamily: 'Helvetica-Bold' },
    score: { fontSize: 14, paddingBottom: 10, fontFamily: 'Helvetica-Bold' },
    subTitle: { paddingBottom: 25 },
    mb20: { marginBottom: 20 },
    mb10: { marginBottom: 10 },
    image: { height: 300, width: 'auto' },
    subImage: {
      width: 50,
      height: 25,
      marginTop: 300,
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 12,
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
    link: {
      color: 'blue',
      textDecoration: 'underline',
      paddingTop: 30,
    },
    radarImage: {
      width: 260,
      height: 260,
      paddingBottom: 30,
    },
    btnLink: {
      backgroundColor: '#0d63fe',
      color: '#FFFFFF',
      padding: 10,
      borderRadius: 5,
      textAlign: 'center',
      width: 200,
      marginTop: 30,
      textDecoration: 'none',
    },
    bold: {
      fontFamily: 'Helvetica-Bold',
    },
    italic: {
      fontFamily: 'Helvetica-Oblique',
    },
    boldItalic: {
      fontFamily: 'Helvetica-BoldOblique',
    },
  });

  const [cratImageString, setBase64Image] = useState(SKELETON_IMAGE);
  const [currentDate, setCurrentDate] = useState<string>('');

  useEffect(() => {
    if (cratImage) {
      imageToBase64(cratImage).then((base64String) => {
        setBase64Image(base64String);
      });
    }
    const date = new Date();
    const formattedDate = date.toLocaleDateString();
    setCurrentDate(formattedDate);
  }, []);

  const getRadarValue = (header: string) => {
    const filterArray = radarData.filter(
      (e) => e.feature === header && e.product == 'User',
    );
    if (!!filterArray.length) return filterArray[0]['score'];
  };

  const mapHtmlToComponents = (html: string, boldItalic = false) => {
    return parse(html, {
      replace: (domNode: any) => {
        if (domNode.type === 'tag') {
          switch (domNode.name) {
            case 'strong':
            case 'b':
              return (
                <Text style={boldItalic ? styles.boldItalic : styles.bold}>
                  {domNode.children.map((child: any) => child.data).join('')}
                </Text>
              );
            case 'br':
              return <Text>{'\n'}</Text>;
            default:
              return (
                <Text>
                  {domNode.children.map((child: any) => child.data).join('')}
                </Text>
              );
          }
        }
        if (domNode.type === 'text') return <Text>{domNode.data}</Text>;
      },
    });
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page1?.heading}</Text>
          <Text style={styles.subTitle}>{currentDate}</Text>
          <Image style={styles.image} src={cratImageString} />
          <Image style={styles.subImage} src={ibmLogo} />
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page2?.heading}</Text>
          {pdfData?.page2?.desc
            ? mapHtmlToComponents(pdfData?.page2?.desc)
            : ''}
          <Link style={styles.link} src={pdfData?.page2?.link || ''}>
            {pdfData?.page2?.link}
          </Link>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page3?.heading}</Text>
          <Image style={styles.radarImage} src={chartImage} />
          {pdfData?.page3.sections.map((v, i) => (
            <View key={i}>
              <Text
                style={{
                  padding: 10,
                  fontSize: 14,
                  marginBottom: 10,
                  backgroundColor: v.color,
                  fontFamily: 'Helvetica-Bold',
                }}
              >
                {v.heading}
              </Text>
              <Text style={styles.score}>
                {v.scoreLabel}: {getRadarValue(v.heading)}%
              </Text>
              <Text style={styles.subTitle}>{v.desc}</Text>
            </View>
          ))}
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page4?.heading}</Text>
          {pdfData?.page4.sections.map((v, i) => (
            <View key={i}>
              <Text
                style={{
                  padding: 10,
                  fontSize: 14,
                  marginBottom: 10,
                  backgroundColor: v.color,
                  fontFamily: 'Helvetica-Bold',
                }}
              >
                {v.heading}
              </Text>
              <Text style={styles.score}>
                {v.scoreLabel}: {getRadarValue(v.heading)}%
              </Text>
              <Text style={styles.subTitle}>{v.desc}</Text>
            </View>
          ))}
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text
            style={{
              fontSize: 14,
              padding: 10,
              marginBottom: 30,
              backgroundColor: pdfData?.page5?.color,
              fontFamily: 'Helvetica-Bold',
            }}
          >
            {pdfData?.page5?.heading}
          </Text>
          <Text style={styles.title}>
            {pdfData?.page5?.scoreLabel}: {scorePercentage}%
          </Text>
          <Text style={styles.title}>{pdfData?.page5?.scoreKeyLabel}</Text>
          {pdfData?.page5.scoreLimit.map((v, i) => (
            <View key={i}>
              <Text style={styles.mb10}>{v.label}</Text>
              <Text style={styles.mb20}>{v.desc}</Text>
            </View>
          ))}
          <View style={styles.italic}>
            {pdfData?.page5?.footnote
              ? mapHtmlToComponents(pdfData?.page5?.footnote, true)
              : ''}
          </View>
          <Link style={styles.btnLink} src={pdfData?.page5?.footNoteLink || ''}>
            {pdfData?.page5?.footNoteBtnLabel}
          </Link>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
